/**
 * Free Purchase Order & Free Receiving API client.
 */

import { apiFetch } from '../apiFetch'
import {
  ProcurementApiClientError,
  type ProcurementApiErrorResponse,
} from './apiClient'
import type { PurchaseOrderDto } from './types'

export type FreeLayoutColumn = {
  key: string
  label: string
  core: boolean
  type: 'text' | 'number' | 'currency'
  readOnly?: boolean
  width?: number
}

export type FreePoLine = {
  id: string
  po_id?: string
  position?: number
  name: string
  uom: string
  qty: number | string
  unit_price?: number | string | null
  line_total?: number | string
  note?: string | null
  extra?: Record<string, unknown>
  received_qty?: number | string
}

export type CreateFreePoPayload = {
  kind?: 'FREE'
  supplier_id: string
  delivery_date: string
  delivery_site?: string | null
  shipping_address?: string | null
  currency?: string
  payment_terms?: string | null
  vat_rate?: number | string | null
  note?: string | null
  free_layout: FreeLayoutColumn[]
  lines: Array<{
    id?: string
    name: string
    uom: string
    qty: number
    unit_price?: number | null
    note?: string | null
    extra?: Record<string, unknown>
  }>
  signers?: Array<{ title: string; name: string }>
}

export type UpdateFreePoContentPayload = {
  vat_rate?: number | string | null
  free_layout: FreeLayoutColumn[]
  lines: Array<{
    id?: string
    name: string
    uom: string
    qty: number
    unit_price?: number | null
    note?: string | null
    extra?: Record<string, unknown>
  }>
  signers?: Array<{ title: string; name: string }>
}

export type FreePoDetail = PurchaseOrderDto & {
  kind: 'FREE'
  free_layout: FreeLayoutColumn[]
  free_lines: FreePoLine[]
}

export type FreeReceiptLineInput = {
  po_free_line_id: string
  qty: number
}

export type CreateFreeReceiptPayload = {
  lines: FreeReceiptLineInput[]
  note?: string | null
}

export type FreeReceiptLineDto = {
  id: string
  receipt_id: string
  po_free_line_id: string
  qty: number | string
  name?: string
  uom?: string
}

export type FreeReceiptDto = {
  id: string
  code: string
  po_id: string
  status: 'ACTIVE' | 'VOID'
  idempotency_key: string
  payload_hash: string
  received_by: string
  received_at: string
  voided_at?: string | null
  void_reason?: string | null
  note?: string | null
  lines: FreeReceiptLineDto[]
}

export type FreeStockItemDto = {
  id: string
  po_id: string
  po_code?: string
  po_free_line_id: string
  name: string
  uom: string
  details: Array<{ label: string; value: unknown }>
  unit_price: number | string | null
  qty_on_hand: number | string
  qty_received_total: number | string
  created_at: string
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfError(res: Response, body: unknown, fallbackMessage: string): void {
  if (res.ok) return
  const err = body as ProcurementApiErrorResponse
  const code = err?.error?.code ?? 'HTTP_ERROR'
  const message = err?.error?.message ?? `${fallbackMessage} (${res.status})`
  const status = err?.error?.status ?? res.status
  throw new ProcurementApiClientError(code, message, status, err?.error?.details)
}

/**
 * Creates a new FREE Purchase Order.
 * POST /api/purchase-orders (with kind: 'FREE')
 */
export async function createFreePo(payload: CreateFreePoPayload): Promise<FreePoDetail> {
  const res = await apiFetch('/api/purchase-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      kind: 'FREE',
    }),
  })
  const body = await parseJson<{ ok: boolean; data: FreePoDetail } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo PO tự do')
  return (body as { data: FreePoDetail }).data
}

/**
 * Gets detail of a FREE Purchase Order.
 * GET /api/purchase-orders/:id
 */
export async function getFreePo(id: string): Promise<FreePoDetail | null> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: FreePoDetail } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải PO tự do ${id}`)
  return (body as { data: FreePoDetail }).data
}

/**
 * Updates free layout and free lines for a DRAFT FREE Purchase Order.
 * PUT /api/purchase-orders/:id/free-content
 */
export async function updateFreePoContent(
  id: string,
  payload: UpdateFreePoContentPayload,
): Promise<FreePoDetail> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/free-content`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: FreePoDetail } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể cập nhật nội dung PO tự do')
  return (body as { data: FreePoDetail }).data
}

/**
 * Receives items on a FREE Purchase Order.
 * POST /api/purchase-orders/:id/free-receipts
 * Header: Idempotency-Key
 */
export async function receiveFreePo(
  poId: string,
  payload: CreateFreeReceiptPayload,
  idempotencyKey?: string,
): Promise<{ receipt: FreeReceiptDto; idempotencyKey: string }> {
  const key =
    idempotencyKey ||
    `frk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  const res = await apiFetch(
    `/api/purchase-orders/${encodeURIComponent(poId)}/free-receipts`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': key,
      },
      body: JSON.stringify(payload),
    },
  )
  const body = await parseJson<{ ok: boolean; data: FreeReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể ghi nhận nhận hàng tự do')
  return { receipt: (body as { data: FreeReceiptDto }).data, idempotencyKey: key }
}

/**
 * Lists free receipts associated with a PO.
 * GET /api/purchase-orders/:id/free-receipts
 */
export async function listFreeReceiptsByPo(poId: string): Promise<FreeReceiptDto[]> {
  const res = await apiFetch(
    `/api/purchase-orders/${encodeURIComponent(poId)}/free-receipts`,
  )
  const body = await parseJson<{ ok: boolean; data: FreeReceiptDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải danh sách phiếu nhận hàng')
  return (body as { data: FreeReceiptDto[] }).data
}

/**
 * Gets a single free receipt.
 * GET /api/free-receipts/:id
 */
export async function getFreeReceipt(receiptId: string): Promise<FreeReceiptDto | null> {
  const res = await apiFetch(`/api/free-receipts/${encodeURIComponent(receiptId)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: FreeReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải phiếu nhận ${receiptId}`)
  return (body as { data: FreeReceiptDto }).data
}

/**
 * Updates receipt lines with reason.
 * PATCH /api/free-receipts/:id
 */
export async function patchFreeReceipt(
  receiptId: string,
  payload: { lines: Array<{ id: string; qty: number }>; reason: string },
): Promise<FreeReceiptDto> {
  if (!payload.reason || !payload.reason.trim()) {
    throw new ProcurementApiClientError('REASON_REQUIRED', 'Lý do sửa là bắt buộc', 400)
  }
  const res = await apiFetch(`/api/free-receipts/${encodeURIComponent(receiptId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: FreeReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể sửa phiếu nhận hàng')
  return (body as { data: FreeReceiptDto }).data
}

/**
 * Voids a free receipt with reason.
 * POST /api/free-receipts/:id/void
 */
export async function voidFreeReceipt(
  receiptId: string,
  reason: string,
): Promise<FreeReceiptDto> {
  if (!reason || !reason.trim()) {
    throw new ProcurementApiClientError('REASON_REQUIRED', 'Lý do hủy là bắt buộc', 400)
  }
  const res = await apiFetch(`/api/free-receipts/${encodeURIComponent(receiptId)}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: FreeReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể hủy phiếu nhận hàng')
  return (body as { data: FreeReceiptDto }).data
}

/**
 * Lists free stock items.
 * GET /api/free-stock
 */
export async function listFreeStock(filters?: {
  po_id?: string
  q?: string
  limit?: number
  offset?: number
}): Promise<FreeStockItemDto[]> {
  const params = new URLSearchParams()
  if (filters?.po_id) params.set('po_id', filters.po_id)
  if (filters?.q) params.set('q', filters.q)
  if (filters?.limit != null) params.set('limit', String(filters.limit))
  if (filters?.offset != null) params.set('offset', String(filters.offset))

  const qs = params.toString()
  const res = await apiFetch(`/api/free-stock${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: FreeStockItemDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải danh sách hàng tự do')
  return (body as { data: FreeStockItemDto[] }).data
}
